import { useEffect, useRef, useState, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerRepresentativeDeclinePayloadType, CustomerRepresentativeDeclineResultType, ObjectCustomerType } from '@econtractor/types';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import useCustomerAiInfo from '../../model/useCustomerAiInfo';
import { mergeCustomerFromAi } from '../../model/customerAiUtils';
import {
    EMPTY_CUSTOMER,
    isCustomerPersonType,
    mapYouControlToCustomer,
    type YouControlUsr,
} from './customerFormUtils';

interface UseCustomerFormFieldsParams {
    initialCustomer?: ObjectCustomerType;
    resetKey?: string | number;
    readOnly?: boolean;
    edrpouReadOnly?: boolean;
}

const useCustomerFormFields = ({
    initialCustomer,
    resetKey,
    readOnly = false,
    edrpouReadOnly = false,
}: UseCustomerFormFieldsParams = {}) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const [formValues, setFormState] = useState<ObjectCustomerType>({ ...EMPTY_CUSTOMER });
    const formRef = useRef(formValues);
    const revisionRef = useRef(0);
    const activeRef = useRef(true);
    const requestRef = useRef<AbortController | null>(null);
    const ycBusyRef = useRef(false);
    const [isDecliningName, setIsDecliningName] = useState(false);
    const declineBusyRef = useRef(false);
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;

    const updateForm = (update: SetStateAction<ObjectCustomerType>) => {
        const next = typeof update === 'function' ? update(formRef.current) : update;
        formRef.current = next;
        setFormState(next);
    };

    // External replacements (catalog selection) invalidate any pending import/declension.
    const setFormValues = (update: SetStateAction<ObjectCustomerType>) => {
        revisionRef.current += 1;
        updateForm(update);
    };
    const [isSaving, setIsSaving] = useState(false);
    const [isYcBusy, setIsYcBusy] = useState(false);
    const initialRef = useRef(initialCustomer);
    initialRef.current = initialCustomer;

    const {
        refInputFile,
        isBusy: isAiBusy,
        uploadProgress,
        phase: aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    } = useCustomerAiInfo({
        onParsed: (parsed) =>
            setFormValues((prev) => {
                const next = mergeCustomerFromAi(prev, parsed);
                if (edrpouReadOnly) next.edrpouOrIpn = prev.edrpouOrIpn;
                return next;
            }),
    });

    useEffect(() => {
        activeRef.current = true;
        revisionRef.current += 1;
        updateForm({ ...EMPTY_CUSTOMER, ...(initialRef.current || {}) });
        return () => {
            activeRef.current = false;
            revisionRef.current += 1;
            requestRef.current?.abort();
        };
    }, [resetKey]);

    const handleChange = <K extends keyof ObjectCustomerType>(field: K, value: ObjectCustomerType[K]) => {
        if (readOnly || (edrpouReadOnly && field === 'edrpouOrIpn')) return;
        if (field === 'representativeName' || field === 'representativeNameGenitive' || field === 'edrpouOrIpn') {
            revisionRef.current += 1;
        }
        updateForm((prev) => ({
            ...prev,
            ...(field === 'representativeName' && value !== prev.representativeName
                ? { representativeNameGenitive: '' }
                : {}),
            [field]: value,
        }));
    };

    const declineRepresentativeName = async (representativeName: string) => {
        if (!representativeName.trim() || declineBusyRef.current || readOnlyRef.current) return;
        const revision = revisionRef.current;
        const controller = new AbortController();
        requestRef.current = controller;
        declineBusyRef.current = true;
        setIsDecliningName(true);
        const isCurrent = () => activeRef.current && !readOnlyRef.current && revisionRef.current === revision;
        try {
            const job = await runAiJobWithPolling<CustomerRepresentativeDeclinePayloadType, CustomerRepresentativeDeclineResultType>({
                requestType: 'objects.customer.decline-representative-name',
                payload: { representativeName },
                timeoutMs: 180000,
                signal: controller.signal,
            });
            const value = job.result?.representativeNameGenitive;
            // The server can return an error status without a result.
            if (job.error || typeof value !== 'string' || !value.trim()) throw new Error('invalid_declension_result');
            if (!isCurrent()) return;
            updateForm((prev) => ({ ...prev, representativeNameGenitive: value.trim() }));
            showToast(t('customer.representativeDecline.success'), 'success');
        } catch (error) {
            if (isCurrent() && !controller.signal.aborted) {
                showToast(t('customer.representativeDecline.error'), 'warning');
            }
        } finally {
            if (requestRef.current === controller) requestRef.current = null;
            declineBusyRef.current = false;
            if (activeRef.current) setIsDecliningName(false);
        }
    };

    const handleDeclineRepresentativeName = () => {
        if (isSaving || isAiBusy || ycBusyRef.current) return;
        void declineRepresentativeName(String(formRef.current.representativeName || '').trim());
    };

    const handleFetchYouControl = async () => {
        const companyCode = String(formRef.current.edrpouOrIpn || '').trim();
        if (companyCode.length < 5) {
            showToast(t('customer.youControl.messCodeRequired'), 'warning');
            return;
        }
        if (ycBusyRef.current || declineBusyRef.current || isSaving || isAiBusy || readOnly) return;

        const revision = revisionRef.current;
        ycBusyRef.current = true;
        setIsYcBusy(true);
        try {
            const ycData = await apiObject.getYouControlCompany(companyCode);
            if (!activeRef.current || readOnlyRef.current || revisionRef.current !== revision) return;
            if (!ycData) {
                showToast(t('customer.youControl.messError'), 'danger');
                return;
            }
            const mapped = mapYouControlToCustomer(ycData as YouControlUsr, companyCode);
            setFormValues((prev) => {
                const next = mergeCustomerFromAi(prev, mapped);
                if (edrpouReadOnly) next.edrpouOrIpn = prev.edrpouOrIpn;
                return next;
            });
            showToast(t('customer.youControl.messLoaded'), 'success');
            if (mapped.representativeName) await declineRepresentativeName(mapped.representativeName);
        } finally {
            ycBusyRef.current = false;
            if (activeRef.current) setIsYcBusy(false);
        }
    };

    const yesLabel = t('customer.fields.vatYes');
    const noLabel = t('customer.fields.vatNo');
    const ycCodeReady = String(formValues.edrpouOrIpn || '').trim().length >= 5;

    const getPersonTypeLabel = (value?: string) => {
        if (isCustomerPersonType(value)) return t(`customer.personTypes.${value}`);
        return value || '-';
    };

    return {
        t,
        formValues,
        setFormValues,
        readOnly,
        edrpouReadOnly,
        isSaving,
        setIsSaving,
        isYcBusy,
        isAiBusy,
        isDecliningName,
        handleDeclineRepresentativeName,
        ycCodeReady,
        yesLabel,
        noLabel,
        getPersonTypeLabel,
        handleChange,
        handleFetchYouControl,
        refInputFile,
        uploadProgress,
        aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useCustomerFormFields;
